import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { AreaConhecimentoApiService } from '../services/area-conhecimento-api.service';
import { AreaConhecimentoItem } from '@core/models/area-conhecimento-items';
import { FiltroTabelasAdministrativas } from '@tabelasAdministrativas/models/filtro';
import { UiService } from '@app/core/services/ui.service';

@Component({
	templateUrl: './tabela-area-conhecimento.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaAreaConhecimentoComponent implements OnDestroy {
	pesquisarAoCarregar = true;
	habilitarCadastradoUsuario = true;
	habilitarFerramenta = true;
	filtros?: FiltroTabelasAdministrativas;
	limparFormularioPesquisa = false;
	resultado: any[] = [];
	loadingResultado = false;
	totalResultado?: number;
	pagina = 1;
	itensPorPagina = 10;
	primeiroItemResultado = 0;
	colunas: any;
	itemTabela = new AreaConhecimentoItem();
	operacao?: string;
	exibirModalManterTabela = false;
	tituloModalManterTabela = '';
	subscriptions: Subscription[] = [];
	registrosSelecionados: AreaConhecimentoItem[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private tabelaService: AreaConhecimentoApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	prepararCadastrar() {
		this.tituloModalManterTabela = 'Cadastrar área de conhecimento';
		this.itemTabela = new AreaConhecimentoItem();
		this.operacao = 'cadastrar';
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.subscriptions.push(
			this.tabelaService.obterAreaConhecimento(event.id).subscribe(res => {
				this.itemTabela = new AreaConhecimentoItem(res.id, res.nome, res.ativo, res.ferramenta, res.incluido_usuario);
				this.tituloModalManterTabela = 'Alterar área de conhecimento';
				this.operacao = 'alterar';
				this.exibirModalManterTabela = true;
			})
		);
	}

	ativarInativarEmLote(ativar = true) {
		this.messageService.clear();
		if (this.registrosSelecionados.length === 0)
			return;

		const listaIdRegistrosSelecionados: number[] = [];
		for (const item of this.registrosSelecionados) {
			listaIdRegistrosSelecionados.push(item.id!);
		}
		const operacao = ativar ? 'ativada(s)' : 'inativada(s)';
		this.subscriptions.push(
			this.tabelaService
				.ativarInativarAreaConhecimento(listaIdRegistrosSelecionados!, ativar)
				.pipe(
					tap(() => {
						this.criarMensagem('success', `Área(s) de conhecimento ${operacao} com sucesso`);
						this.pesquisar();
					}),
					catchError(err => {
						this.criarMensagem('error', err.error.message);
						return [];
					})
				)
				.subscribe()
		);
	}	

	salvarItem(event: AreaConhecimentoItem) {
		const areaConhecimento = new AreaConhecimentoItem();
		areaConhecimento.id = event.id;
		areaConhecimento.nome = event.nome;
		areaConhecimento.ativo = event.ativo ? true : false;
		areaConhecimento.ferramenta = event.ferramenta ? true : false;
		areaConhecimento.incluido_usuario = event.incluido_usuario ? true : false;
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(areaConhecimento);
		} else if (areaConhecimento.id) {
			this.alterar(areaConhecimento.id, areaConhecimento);
		}
		this.operacao = '';
		this.exibirModalManterTabela = false;
	}

	executarPesquisar(event: any) {
		this.filtros = new FiltroTabelasAdministrativas();
		this.filtros.nome = event.nome;
		this.filtros.ativo = event.ativo;
		this.filtros.ferramenta = event.ferramenta;
		this.filtros.incluido_usuario = event.incluido_usuario;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
		this.criarColunas();
	}

	limparFormulario() {
		if (this.pesquisarAoCarregar) {
			this.executarPesquisar(new FiltroTabelasAdministrativas());
			this.limparFormularioPesquisa = true;
		} else {
			this.resultado = [];
		}
	}

	excluirItem(event: any) {
		const instituicao = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir a área de conhecimento ' + instituicao.nome + '?',
			accept: async () => {
				this.tabelaService.excluirAreaConhecimento(event.id).subscribe(
					async (res: any) => {
						this.exibirMensagemSucesso(res);
					},
					err => {
						this.criarMensagem('error', err.error.message);
					}
				);
			}
		});
	}

	fecharModal() {
		this.exibirModalManterTabela = false;
		this.itemTabela = new AreaConhecimentoItem();
		this.operacao = 'cadastrar';
	}

	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.primeiroItemResultado = event.first;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
	}

	private pesquisar() {
		this.resultado = [];
		this.loadingResultado = true;
		if (this.filtros) {
			this.subscriptions.push(
				this.tabelaService.obterAreasConhecimento(this.filtros, this.pagina, this.itensPorPagina).subscribe(
					(resultado: any) => {
						this.loadingResultado = false;
						this.totalResultado = resultado.metadata.total;
						this.pagina = resultado.metadata.page;
						this.tratarDados(resultado.items);
					},
					err => {
						this.totalResultado = 0;
						this.resultado = [];
						this.criarMensagem('error', err.error.message);
					}
				)
			);
		}
	}

	private criarColunas() {
		this.colunas = [
			{ field: 'id', header: 'id' },
			{ field: 'nome', header: 'Área de conhecimento' },
			{ field: 'ativo', header: 'Ativo' },
			{ field: 'ferramenta', header: 'Ferramenta' },
			{ field: 'incluido_usuario', header: 'Cadastrado pelo usuário?' }
		];
	}

	private tratarDados(itens: any[]) {
		for (const item of itens) {
			const resultadoTratado = {
				id: item.id,
				nome: item.nome,
				ativo: item.ativo ? 'Sim' : 'Não',
				ferramenta: item.ferramenta ? 'Sim' : 'Não',
				incluido_usuario: item.incluido_usuario ? 'Sim' : 'Não'
			};
			this.resultado.push(resultadoTratado);
		}
	}

	private salvarNovo(areaConhecimento: AreaConhecimentoItem) {
		this.subscriptions.push(
			this.tabelaService.incluirAreaConhecimento(areaConhecimento).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private alterar(id: number, areaConhecimento: AreaConhecimentoItem) {
		this.subscriptions.push(
			this.tabelaService.alterarAreaConhecimento(id, areaConhecimento).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.pesquisar();
	}
}
